import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult, GRAPHQL_AUTH_MODE } from "@aws-amplify/api";

// Configure Amplify
import Amplify from "aws-amplify";
import awsmobile from "../../aws-exports";

import formatResult from "../ReturnType/ReturnType";
import { CreateCompetitionInput, CreateCompetitionMutation, DeleteCompetitionInput, DeleteCompetitionMutation, GetCompetitionQuery, ListCompetitionsQuery, UpdateCompetitionInput, UpdateCompetitionMutation } from "../../API";
import moment from "moment";
import { Competition } from "../../models";

Amplify.configure(awsmobile);

/**
* Handle creating a new record in the database for type: competition. 
* 
* @param {object}  input                   Check schema for input.
* @param {string}  authMode                API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {string} Returns the newly created competition.
*/
export async function createCompetition(input: CreateCompetitionInput, authMode?: GRAPHQL_AUTH_MODE) {
   if (!input) return formatResult(false, "Competition", "No input", "Create Competition received no input.");
   try {
        const fullInput: CreateCompetitionInput = {
            ...input,
            createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
            updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        };
       let result;
       if (!authMode) result = (await API.graphql(graphqlOperation(mutations.createCompetition, { input: fullInput }))) as GraphQLResult<CreateCompetitionMutation>;
       else result = (await API.graphql({
           query: mutations.createCompetition,
           variables: {
               input: fullInput
           },
           authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
       })) as GraphQLResult<CreateCompetitionMutation>;
       const competition = result.data?.createCompetition;
       return formatResult(true, "Competition", competition, "Successfully created the competition.");
   } catch (error: any) {
       return formatResult(false, "Competition", error, "Error creating record in the database for type: competition");
   }
}

/**
* Handle updating a new record in the database for type: competition. 
* 
* @param {object}  input                   Check schema for input.
* @param {string}  authMode                API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {string} Returns the newly updated competition.
*/
export async function updateCompetition(input: UpdateCompetitionInput, authMode?: GRAPHQL_AUTH_MODE) {
    if (!input) return formatResult(false, "Competition", "No input", "Update Competition received no input.");
    try {
            const fullInput: UpdateCompetitionInput = {
                ...input,
                updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
            };
        let result;
        if (!authMode) result = (await API.graphql(graphqlOperation(mutations.updateCompetition, { input: fullInput }))) as GraphQLResult<UpdateCompetitionMutation>;
        else result = (await API.graphql({
            query: mutations.updateCompetition,
            variables: {
                input: fullInput
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<UpdateCompetitionMutation>;
        const competition = result.data?.updateCompetition as Competition;
        return formatResult(true, "Competition", competition, "Successfully updated the competition.");
    } catch (error: any) {
        return formatResult(false, "Competition", error, "Error updating record in the database for type: competition");
    }
}

/**
* Handle deleting a new record in the database for type: competition. 
* 
* @param {object}  input                   Just requires the ID of the object.
* @param {string}  authMode                API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {string} Returns the newly deleted competition.
*/
export async function deleteCompetition(input: DeleteCompetitionInput, authMode?: GRAPHQL_AUTH_MODE) {
   if (!input) return formatResult(false, "Competition", "No input", "Delete Competition received no input.");
   try {
       let result;
       if (!authMode) result = (await API.graphql(graphqlOperation(mutations.deleteCompetition, { input: input }))) as GraphQLResult<DeleteCompetitionMutation>;
       else result = (await API.graphql({
           query: mutations.deleteCompetition,
           variables: {
               input: input
           },
           authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
       })) as GraphQLResult<DeleteCompetitionMutation>;
       const competition = result.data?.deleteCompetition;
       return formatResult(true, "Competition", competition, "Successfully deleted the competition.");
   } catch (error: any) {
       return formatResult(false, "Competition", error, "Error deleting record in the database for type: competition");
   }
}

/**
* Get all records in the database for type: competition. 
* 
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the competition object
*/
export async function getAllCompetitions(authMode?: GRAPHQL_AUTH_MODE) {
    try {
        const result = (await API.graphql({
            query: queries.listCompetitions,
            variables: {
                limit: 1000
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<ListCompetitionsQuery>;
        
        let items = result.data?.listCompetitions?.items as Competition[];
        let nextToken = result.data?.listCompetitions?.nextToken;

        while (nextToken) {
            const nextResult = await API.graphql({
                query: queries.listCompetitions,
                variables: {
                    limit: 100,
                    nextToken
                },
                authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
            }) as GraphQLResult<ListCompetitionsQuery>;

            const nextItems = nextResult.data?.listCompetitions?.items as Competition[];
            if (nextItems && nextItems.length) {
                items = items?.concat(nextItems);
            }

            nextToken = nextResult.data?.listCompetitions?.nextToken;
        }

        const sorted = items.sort((a,b) => {
            const aDate: any = moment(a.startDate || "").format("YYYYMMDDHHmmss")
            const bDate: any = moment(b.startDate || "").format("YYYYMMDDHHmmss");
            return bDate - aDate;
        });
        if (sorted) {
            return formatResult(true, "Competition", sorted, "Successfully got the competitions.");
        } else {
            return formatResult(true, "Competition", items, "Successfully got the competitions.");
        }
    } catch (error: any) {
        return formatResult(false, "Competition", error, "Error reading record in the database for type: competitions");
    }
}

/**
* Read a specific record in the database for type: competition. 
* 
* @param {string}  id                  The competition id.
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the competition object
*/
export async function getCompetitionById(id: string, authMode?: GRAPHQL_AUTH_MODE) {
   try {
       const result = (await API.graphql({
           query: queries.getCompetition,
           variables: {
               id: id
           },
           authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
       })) as GraphQLResult<GetCompetitionQuery>;
       const competition = result.data?.getCompetition;
       return formatResult(true, "Competition", competition, "Successfully got the competition.");
   } catch (error: any) {
       return formatResult(false, "Competition", error, "Error reading record in the database for type: competition");
   }
}

/**
* Get all records in the database that match the given criteria for type: competition. 
* 
* @param {string}  status              The status of the competition will be published or draft
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the competition object
*/
export async function getCompetitionsByStatus(status: string, authMode?: GRAPHQL_AUTH_MODE) {
    try {
        const filter = {status: {eq: status}};
        const result = (await API.graphql({
            query: queries.listCompetitions,
            variables: {
                limit: 1000,
                filter
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<ListCompetitionsQuery>;
        let items = result.data?.listCompetitions?.items as Competition[];
        let nextToken = result.data?.listCompetitions?.nextToken;

        while (nextToken) {
            const nextResult = await API.graphql({
                query: queries.listCompetitions,
                variables: {
                    limit: 100,
                    filter,
                    nextToken
                },
                authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
            }) as GraphQLResult<ListCompetitionsQuery>;

            const nextItems = nextResult.data?.listCompetitions?.items as Competition[];
            if (nextItems && nextItems.length) {
                items = items?.concat(nextItems);
            }

            nextToken = nextResult.data?.listCompetitions?.nextToken;
        }
        
        const sorted = items.sort((a,b) => {
            const aDate: any = moment(a.startDate || "").format("YYYYMMDDHHmmss")
            const bDate: any = moment(b.startDate || "").format("YYYYMMDDHHmmss");
            return bDate - aDate;
        });
        if (sorted) {
            return formatResult(true, "Competition", sorted, "Successfully got the competitions.");
        } else {
            return formatResult(true, "Competition", items, "Successfully got the competitions.");
        }
    } catch (error: any) {
        return formatResult(false, "Competition", error, "Error reading record in the database for type: competitions");
    }
}

/**
* Get all records in the database that match the given criteria for type: competition. 
* 
* @param {string}  personId            The person id of the "owner" of the competition
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the competition object
*/
export async function getCompetitionsByCreator(personId: string, authMode?: GRAPHQL_AUTH_MODE) {
    try {
        const filter = {createdBy: {eq: personId}};
        const result = (await API.graphql({
            query: queries.listCompetitions,
            variables: {
                limit: 1000,
                filter
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<ListCompetitionsQuery>;
        let items = result.data?.listCompetitions?.items as Competition[];
        let nextToken = result.data?.listCompetitions?.nextToken;

        while (nextToken) {
            const nextResult = await API.graphql({
                query: queries.listCompetitions,
                variables: {
                    limit: 100,
                    filter,
                    nextToken
                },
                authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
            }) as GraphQLResult<ListCompetitionsQuery>;

            const nextItems = nextResult.data?.listCompetitions?.items as Competition[];
            if (nextItems && nextItems.length) {
                items = items?.concat(nextItems);
            }

            nextToken = nextResult.data?.listCompetitions?.nextToken;
        }
        
        const sorted = items.sort((a,b) => {
            const aDate: any = moment(a.startDate || "").format("YYYYMMDDHHmmss")
            const bDate: any = moment(b.startDate || "").format("YYYYMMDDHHmmss");
            return bDate - aDate;
        });
        if (sorted) {
            return formatResult(true, "Competition", sorted, "Successfully got the competitions.");
        } else {
            return formatResult(true, "Competition", items, "Successfully got the competitions.");
        }
    } catch (error: any) {
        return formatResult(false, "Competition", error, "Error reading record in the database for type: competitions");
    }
}

/**
* Get all records in the database that match the given criteria for type: competition. 
* 
* @param {string}  teamId               The team id of the competition
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the competition object
*/
export async function getCompetitionsByTeamId(teamId: string, authMode?: GRAPHQL_AUTH_MODE) {
    try {
        const filter = {teamId: {eq: teamId}};
        const result = (await API.graphql({
            query: queries.listCompetitions,
            variables: {
                limit: 1000,
                filter
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<ListCompetitionsQuery>;
        let items = result.data?.listCompetitions?.items as Competition[];
        let nextToken = result.data?.listCompetitions?.nextToken;

        while (nextToken) {
            const nextResult = await API.graphql({
                query: queries.listCompetitions,
                variables: {
                    limit: 1000,
                    filter,
                    nextToken
                },
                authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
            }) as GraphQLResult<ListCompetitionsQuery>;

            const nextItems = nextResult.data?.listCompetitions?.items as Competition[];
            if (nextItems && nextItems.length) {
                items = items?.concat(nextItems);
            }

            nextToken = nextResult.data?.listCompetitions?.nextToken;
        }
        
        const sorted = items.sort((a,b) => {
            const aDate: any = moment(a.startDate || "").format("YYYYMMDDHHmmss")
            const bDate: any = moment(b.startDate || "").format("YYYYMMDDHHmmss");
            return bDate - aDate;
        });
        if (sorted) {
            return formatResult(true, "Competition", sorted, "Successfully got the competitions.");
        } else {
            return formatResult(true, "Competition", items, "Successfully got the competitions.");
        }
    } catch (error: any) {
        return formatResult(false, "Competition", error, "Error reading record in the database for type: competitions");
    }
}